// =========================================================================
// Where a job goes when it is clicked. Fork-owned (zone C), L5 lane.
//
// The demo gave every row of `buildJobs` an `open()` closure (source lines
// 2947–2966) and every job card and "In this module now" row called it. The
// engine keeps that closure as `OemJob.action`, a typed `{ view, sub }`. This
// file turns it into a `SeedNav`, so the job board and the module page open a
// job the same way `useSeedNav()` opens everything else.
//
// Two shapes arrive:
//
// | `action`                          | demo                                  | here            |
// |-----------------------------------|---------------------------------------|-----------------|
// | `{ view: 'alerts', sub: id }` etc | `show('alerts', id)`                  | `navShow`       |
// | `{ view: 'audit', sub: 'cases:N' }` | `audit('cases', { case_no: N })`    | `navAudit`      |
//
// Transformation layer — pure.
// =========================================================================

import type { OemJob } from '../../shared/oem-engine';
import { routeForView } from '../../shared/oem-routes';
import { navAudit, navShow, type SeedNav } from '../../shared/seed/modules';

/** The key column each audited table is filtered on. */
const AUDIT_KEY: Readonly<Record<string, string>> = {
  cases: 'case_no',
};

/**
 * jobNav(job) → SeedNav
 * Projection rule: an `audit` action carries `'<table>:<key>'` in `sub`
 *   (see `OemJob` in oem-engine) and becomes an equality filter on that
 *   table's key column; every other action is a `show()`.
 */
export function jobNav(job: OemJob): SeedNav {
  const { view, sub } = job.action;
  if (view === 'audit' && sub) {
    const at = sub.indexOf(':');
    const table = at === -1 ? sub : sub.slice(0, at);
    const value = at === -1 ? '' : sub.slice(at + 1);
    const field = AUDIT_KEY[table];
    return field && value ? navAudit(table, [{ field, op: 'eq', value }]) : navAudit(table);
  }
  return navShow(view, sub);
}

/** Demo `j.run || ('job:' + j.id)` — a worked run, or a generated one. */
export const jobRunKey = (job: OemJob): string => job.run ?? `job:${job.id}`;

/** The case trace for one job: `show('jobrun', key)`. */
export const jobRunHref = (job: OemJob): string =>
  routeForView('jobrun', jobRunKey(job)) ?? '/workflows/case-trace';
